import React, {useEffect, useState} from "react";
import {Modal, Form, Input, Select, DatePicker, Button, InputNumber, message, Upload} from "antd";
import { ModalCadastroChargebackProps, DisputesRequestDto, PatchDisputeStatusDto } from "../../types";
import useCurrentUser from "../../../../../hooks/useCurrentUser";
import dayjs from "dayjs";
import {UploadFile} from "antd/es/upload/interface";
import {UploadOutlined} from "@ant-design/icons";

const disputeStatusOptions = [
  { value: 0, label: "Em disputa" },
  { value: 1, label: "Procedente" },
  { value: 2, label: "Improcedente" }
];

const brandOptions = [
  { value: "VISA", label: "VISA" },
  { value: "MASTERCARD", label: "MASTERCARD" },
  { value: "ELO", label: "ELO" },
  { value: "AMEX", label: "AMEX" },
  { value: "MAESTRO", label: "MAESTRO" },
  { value: "HIPERCARD", label: "HIPERCARD" },
];

const paymentOptions = [
  { value: "PHYSICAL", label: "PHYSICAL" },
  { value: "DIGITAL", label: "DIGITAL" },
];

const acquirerOptions = [
  { value: "3", label: "Adiq" },
  { value: "10", label: "Rede" },
  { value: "99", label: "Valori" },
];

const productOptions = [
  { value: "Crédito à Vista", label: "Crédito à Vista" },
  { value: "Parcelamento sem Juros", label: "Parcelamento sem Juros" },
  { value: "Débito", label: "Débito" },
];

const lockedFieldStyle: React.CSSProperties = {
  backgroundColor: "#f5f5f5",
  color: "#00000040",
  cursor: "not-allowed",
};

export const ModalCadastroChargeback: React.FC<ModalCadastroChargebackProps> = ({
  open,
  onClose,
  onCreate,
  onUpdate,
  mode,
  initialData,
}) => {
  const [form] = Form.useForm();
  const user = useCurrentUser();
  const [loading, setLoading] = React.useState(false);
  const isView = mode === "view";
  const isEdit = mode === "edit";
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const isFinalStatus =
    isEdit &&
    initialData &&
    (Number(initialData.disputeStatus) === 1 || Number(initialData.disputeStatus) === 2);

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        MerchantId: initialData.merchantId,
        Nsu: initialData.nsu,
        Authorization: initialData.authorization,
        PaymentDate: initialData.paymentDate ? dayjs(initialData.paymentDate) : null,
        DateLimit: initialData.dateLimit ? dayjs(initialData.dateLimit) : null,
        PaymentValue: formatCurrencyBR(initialData.paymentValue),
        LiquidValue: formatCurrencyBR(initialData.liquidValue),
        ChargebackValue: formatCurrencyBR(initialData.chargebackValue),
        CaptureChannel: initialData.captureChannel,
        ProductName: initialData.productName,
        Parcels: initialData.parcels,
        Brand: initialData.brand,
        Acquirer: initialData.acquirer,
        DisputeReason: initialData.disputeReason,
        DisputeStatus: Number(initialData.disputeStatus),
      });
    } else {
      form.resetFields();
    }
  }, [initialData, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (isEdit && initialData && onUpdate) {
        const payload: PatchDisputeStatusDto = {
          ChargebackId: Number(initialData.id),
          MerchantId: initialData.merchantId,
          Nsu: initialData.nsu,
          Authorization: initialData.authorization,
          OldDisputeStatus: Number(initialData.disputeStatus),
          NewDisputeStatus: isFinalStatus ? Number(initialData.disputeStatus) : Number(values.DisputeStatus),
          CreatedBy: user.email,
          ChargebackPdf: fileList.length > 0 && fileList[0].originFileObj ? fileList[0].originFileObj : undefined,
          DisputeReason: isFinalStatus ? initialData.disputeReason : values.DisputeReason,
        };

        await onUpdate(payload);
        message.success("Chargeback atualizado com sucesso!");
      } else if (mode === "create" && onCreate) {
        const payload: DisputesRequestDto = {
          MerchantId: values.MerchantId,
          Nsu: values.Nsu,
          Authorization: values.Authorization,
          PaymentDate: values.PaymentDate.startOf("day").toISOString(),
          DateLimit: values.DateLimit ? values.DateLimit.startOf("day").toISOString() : new Date().toISOString(),
          PaymentValue: parseToNumber(values.PaymentValue),
          LiquidValue: parseToNumber(values.LiquidValue),
          ChargebackValue: parseToNumber(values.ChargebackValue),
          CaptureChannel: values.CaptureChannel,
          ProductName: values.ProductName,
          Parcels: Number(values.Parcels),
          DisputeReason: values.DisputeReason,
          DisputeStatus: Number(values.DisputeStatus),
          Brand: values.Brand,
          Acquirer: Number(values.Acquirer),
          CreatedBy: user.email,
          ChargebackPdf: fileList.length > 0 ? fileList[0].originFileObj : undefined
        };

        await onCreate(payload);
        message.success("Chargeback criado com sucesso!");
      }

      form.resetFields();
      setFileList([]);
      onClose();
    } catch (error) {
      console.error("Erro ao salvar chargeback:", error);
      message.error("Ocorreu um erro ao salvar o chargeback.");
    } finally {
      setLoading(false);
    }
  };

 const formatCurrencyBR = (value: number) => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const parseToNumber = (value: string) => {
  return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
};

  const pdfUpload = (
    <Form.Item
      label="Anexo"
      name="ChargebackPdf "
      valuePropName="fileList"
      getValueFromEvent={(e) => {
        if (Array.isArray(e)) return e;
        return e?.fileList;
      }}
      rules={[
        { required: false },
        {
          validator: (_, value) => {
            if (!fileList || fileList.length === 0) return Promise.resolve();
            const file = fileList[0].originFileObj;
            if (!file) return Promise.resolve();
            if (file.type !== 'application/pdf') return Promise.reject('O arquivo deve ser um PDF');
            if (file.size > 10 * 1024 * 1024) return Promise.reject('O arquivo não pode ser maior que 10MB');
            return Promise.resolve();
          }
        },
      ]}
    >
      <Upload
        beforeUpload={(file) => {
          const uploadFile: UploadFile = {
            uid: file.uid || `${Date.now()}`,
            name: file.name,
            status: 'done',
            originFileObj: file,
          };
          setFileList([uploadFile]);
          return false;
        }}
        onRemove={() => setFileList([])}
        fileList={fileList}
        maxCount={1}
        accept=".pdf,application/pdf"
      >
        <Button icon={<UploadOutlined />}>Selecionar PDF (máx. 10MB)</Button>
      </Upload>
    </Form.Item>
  );

  return (
    <Modal
      title={mode === "create" ? "Cadastrar Chargeback" : isEdit ? "Editar Chargeback" : "Visualizar Chargeback"}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ Parcels: 1, DisputeStatus: 0 }}>

        {/* Campos sempre bloqueados no modo editar */}
        <Form.Item
          label="Código Logista"
          name="MerchantId"
          rules={[{ required: !isEdit && !isView }]}
        >
          <Input
            readOnly={isView || isEdit}
            style={isEdit ? lockedFieldStyle : undefined}
          />
        </Form.Item>

        <Form.Item
          label="NSU"
          name="Nsu"
          rules={[{ required: !isEdit && !isView }]}
        >
          <Input
            readOnly={isView || isEdit}
            style={isEdit ? lockedFieldStyle : undefined}
          />
        </Form.Item>

        <Form.Item
          label="Código Autorização"
          name="Authorization"
          rules={[{ required: !isEdit && !isView }]}
        >
          <Input
            readOnly={isView || isEdit}
            style={isEdit ? lockedFieldStyle : undefined}
          />
        </Form.Item>

        <Form.Item
          label="Data da Venda"
          name="PaymentDate"
          rules={[{ required: !isEdit && !isView }]}
        >
          <DatePicker
            disabled={isView || isEdit}
            style={{ width: "100%", ...(isEdit ? lockedFieldStyle : {}) }}
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item
          label="Data Limite para Retorno"
          name="DateLimit"
          rules={[{ required: !isEdit && !isView }]}
        >
          <DatePicker
            disabled={isView || isEdit}
            style={{ width: "100%", ...(isEdit ? lockedFieldStyle : {}) }}
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item
          label="Valor do Pagamento"
          name="PaymentValue"
          rules={[{ required: !isEdit && !isView }]}
        >
          <Input
            readOnly={isView || isEdit}
            style={isEdit ? lockedFieldStyle : undefined}
            onBlur={(e) => {
              if (!isEdit && !isView) {
                const num = parseToNumber(e.target.value);
                form.setFieldsValue({ PaymentValue: formatCurrencyBR(num) });
              }
            }}
          />
        </Form.Item>

        <Form.Item label="Valor Líquido" name="LiquidValue" rules={[{ required: !isEdit && !isView }]}>
          <Input
            readOnly={isView || isEdit}
            style={isEdit ? lockedFieldStyle : undefined}
            onBlur={(e) => {
              if (!isEdit && !isView) {
                const num = parseToNumber(e.target.value);
                form.setFieldsValue({ LiquidValue: formatCurrencyBR(num) });
              }
            }}
          />
        </Form.Item>

        <Form.Item
          label="Valor do Chargeback"
          name="ChargebackValue"
          rules={[{ required: !isEdit && !isView }]}
        >
          <Input
            readOnly={isView || isEdit}
            style={isEdit ? lockedFieldStyle : undefined}
            onBlur={(e) => {
              if (!isEdit && !isView) {
                const num = parseToNumber(e.target.value);
                form.setFieldsValue({ ChargebackValue: formatCurrencyBR(num) });
              }
            }}
          />
        </Form.Item>

        <Form.Item
          label="Tipo Pagamento"
          name="ProductName"
          rules={[{ required: !isEdit && !isView }]}
        >
          {isView || isEdit ? (
            <Input readOnly style={isEdit ? lockedFieldStyle : undefined} />
          ) : (
            <Select allowClear options={productOptions} />
          )}
        </Form.Item>

        <Form.Item
          label="Número de Parcelas"
          name="Parcels"
          rules={[{ required: !isEdit && !isView }]}
        >
          <InputNumber
            readOnly={isView || isEdit}
            min={1}
            style={{ width: "100%", ...(isEdit ? lockedFieldStyle : {}) }}
          />
        </Form.Item>

        <Form.Item
          label="Canal de Pagamento"
          name="CaptureChannel"
          rules={[{ required: !isEdit && !isView }]}
        >
          {isView || isEdit ? (
            <Input readOnly style={isEdit ? lockedFieldStyle : undefined} />
          ) : (
            <Select allowClear options={paymentOptions} />
          )}
        </Form.Item>

        <Form.Item
          label="Bandeira"
          name="Brand"
          rules={[{ required: !isEdit && !isView }]}
        >
          {isView || isEdit ? (
            <Input readOnly style={isEdit ? lockedFieldStyle : undefined} />
          ) : (
            <Select allowClear options={brandOptions} />
          )}
        </Form.Item>

        <Form.Item
          label="Adquirente"
          name="Acquirer"
          rules={[{ required: !isEdit && !isView }]}
        >
          {isView || isEdit ? (
            <Input readOnly style={isEdit ? lockedFieldStyle : undefined} />
          ) : (
            <Select allowClear options={acquirerOptions} />
          )}
        </Form.Item>

        {/* Motivo: editável apenas em modo edit com status "Em disputa" */}
        <Form.Item label="Motivo da Contestação" name="DisputeReason">
          <Input.TextArea
            readOnly={isView || isFinalStatus}
            style={isFinalStatus ? lockedFieldStyle : undefined}
            rows={3}
          />
        </Form.Item>

        {/* Status: editável apenas em modo edit com status "Em disputa" */}
        <Form.Item
          label="Status da Contestação"
          name="DisputeStatus"
          rules={[{ required: !isView }]}
        >
          {isView || isFinalStatus ? (
            <Input
              readOnly
              style={isFinalStatus ? lockedFieldStyle : undefined}
                />
          ) : (
            <Select options={disputeStatusOptions} />
          )}
        </Form.Item>

        {/* PDF: sempre disponível em edit e create */}
        {!isView && pdfUpload}

        {!isView && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {isEdit ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        )}
      </Form>
    </Modal>
  );
};
